import React, { useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, Stepper, Step, StepLabel, Paper, Container, Typography, Link } from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import LayerSetup from './components/LayerSetup';
import ImageUpload from './components/ImageUpload';
import { ConfigSetup } from './components/ConfigSetup';
import GenerateCollection from './components/GenerateCollection';
import Header from './components/Header';
import AppStepper from './components/AppStepper';
import HomePage from './components/HomePage';
import TutorialPage from './components/TutorialPage';
import TermsOfService from './components/TermsOfService';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#6200ea',
    },
    secondary: {
      main: '#03dac6',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

const Footer: React.FC = () => {
  return (
    <Box 
      component="footer" 
      sx={{ 
        p: 3, 
        mt: 4,
        bgcolor: 'background.paper', 
        borderTop: 1, 
        borderColor: 'divider',
        textAlign: 'center' 
      }}
    >
      <Typography variant="body2" color="text.secondary" paragraph>
        This app is a fork of HashLips Art Engine, designed to provide a user-friendly UI 
        for NFT generation and enhance the overall user experience.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Developed by Quickosss - <Link href="https://x.com/0x_Quickosss" target="_blank" rel="noopener noreferrer">@0x_Quickosss</Link>
      </Typography>
    </Box>
  );
};

const steps = ['Configuration', 'Layer Setup', 'Image Upload', 'Generation'];

const StepContent: React.FC = () => {
  const { currentStep } = useAppContext();

  switch (currentStep) {
    case 0:
      return <ConfigSetup />;
    case 1:
      return <LayerSetup />;
    case 2:
      return <ImageUpload />;
    case 3:
      return <GenerateCollection />;
    default:
      return <ConfigSetup />;
  }
};

const AppContent: React.FC = () => {
  const { currentStep } = useAppContext();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
        
        <Paper elevation={3} sx={{ p: 3 }}>
          <StepContent />
        </Paper>
      </Container>
      
      <Box component="footer" sx={{ p: 2, bgcolor: 'background.paper', textAlign: 'center' }}>
        HashLips Web Art Engine - Easily create your NFT collections
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  const [showHomePage, setShowHomePage] = useState(true);
  const [showTutorialPage, setShowTutorialPage] = useState(false);

  const handleGetStarted = () => {
    setShowHomePage(false);
    setShowTutorialPage(false);
  };

  const handleNavigateHome = () => {
    setShowHomePage(true);
    setShowTutorialPage(false);
  };

  const handleNavigateApp = () => {
    setShowHomePage(false);
    setShowTutorialPage(false);
  };

  const handleNavigateTutorial = () => {
    setShowHomePage(false);
    setShowTutorialPage(true);
  };

  const handleBackToHome = () => {
    setShowHomePage(true);
    setShowTutorialPage(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/terms" element={
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
              <Header 
                onNavigateHome={handleNavigateHome} 
                onNavigateApp={handleNavigateApp} 
                onNavigateTutorial={handleNavigateTutorial}
                currentPage="home" 
              />
              <TermsOfService />
            </Box>
          } />
          {showHomePage ? (
            <Route path="/" element={
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Header 
                  onNavigateHome={handleNavigateHome} 
                  onNavigateApp={handleNavigateApp} 
                  onNavigateTutorial={handleNavigateTutorial}
                  currentPage="home" 
                />
                <HomePage 
                  onGetStarted={handleGetStarted} 
                  onTutorial={handleNavigateTutorial} 
                />
              </Box>
            } />
          ) : showTutorialPage ? (
            <Route path="/" element={
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Header 
                  onNavigateHome={handleNavigateHome} 
                  onNavigateApp={handleNavigateApp} 
                  onNavigateTutorial={handleNavigateTutorial}
                  currentPage="tutorial" 
                />
                <TutorialPage onBackToHome={handleBackToHome} />
              </Box>
            } />
          ) : (
            <Route path="/" element={
              <AppProvider>
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <Header 
                    onNavigateHome={handleNavigateHome} 
                    onNavigateApp={handleNavigateApp} 
                    onNavigateTutorial={handleNavigateTutorial}
                    currentPage="app" 
                  />
                  <Container maxWidth="lg" sx={{ flexGrow: 1 }}>
                    <Box my={4}>
                      <AppStepper />
                    </Box>
                  </Container>
                  <Footer />
                </Box>
              </AppProvider>
            } />
          )}
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;
